import {
  isValidNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

/**
 * Normalizes a phone number by removing formatting characters
 * (spaces, dashes, parentheses) for comparison purposes
 *
 * @param phone - The phone number to normalize (can be string, null, or undefined)
 * @returns The normalized phone number string, or empty string if input is null/undefined
 *
 * @example
 * normalizePhone('(555) 123-4567') // returns '5551234567'
 * normalizePhone('555 123 4567') // returns '5551234567'
 * normalizePhone(null) // returns ''
 */
export function normalizePhone(phone: string | null | undefined): string {
  return phone?.replace(/[\s\-()]/g, '') || '';
}

/**
 * Formats a phone number for display, matching the formatting the booking
 * form's phone input applies as the user types.
 *
 * The API returns phone numbers in E.164 (e.g. '+15551234567'), which is not
 * meant to be read by humans. US numbers are formatted nationally, and other
 * countries use their own grouping rules with the country code kept visible
 * (a shop abroad may need to be dialled internationally).
 *
 * Numbers that cannot be parsed, or that parse but are not valid, are returned
 * unchanged rather than being forced into a mask. Formatting an invalid number
 * would fabricate structure that isn't there — `formatNational` does not
 * validate, so '+10081598229' would otherwise render as the plausible-looking
 * but meaningless '(008) 159-8229'.
 *
 * @param phone - The phone number to format (can be string, null, or undefined)
 * @returns The formatted phone number, the original string if it cannot be
 * formatted, or empty string if input is null/undefined
 *
 * @example
 * formatPhoneForDisplay('+15551234567') // returns '(555) 123-4567'
 * formatPhoneForDisplay('+541153191757') // returns '+54 11 5319-1757'
 * formatPhoneForDisplay('+10081598229') // returns '+10081598229' (invalid)
 * formatPhoneForDisplay(null) // returns ''
 */
export function formatPhoneForDisplay(
  phone: string | null | undefined,
): string {
  if (!phone) return '';

  const parsed = parsePhoneNumberFromString(phone);
  if (!parsed?.isValid()) return phone;

  return parsed.country === 'US'
    ? parsed.formatNational()
    : parsed.formatInternational();
}

/**
 * Formats a phone number for display WITH its country calling code always
 * visible (e.g. "+1 (555) 123-4567"), unlike `formatPhoneForDisplay`, which
 * drops the code for US numbers. Use where the surrounding UI has no other
 * indication of the number's country (e.g. a public/guest-facing card).
 *
 * Numbers that cannot be parsed, or that parse but are not valid, are
 * returned unchanged — see `formatPhoneForDisplay` for why.
 *
 * @example
 * formatPhoneWithCountryCode('+15551234567') // returns '+1 (555) 123-4567'
 * formatPhoneWithCountryCode('+541153191757') // returns '+54 011 5319-1757'
 * formatPhoneWithCountryCode(null) // returns ''
 */
export function formatPhoneWithCountryCode(
  phone: string | null | undefined,
): string {
  if (!phone) return '';

  const parsed = parsePhoneNumberFromString(phone);
  if (!parsed?.isValid()) return phone;

  return `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
}

export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone) return false;

  const valid = isValidNumber(phone);
  const length = validatePhoneNumberLength(phone);

  return Boolean(valid && !length);
}
